import posthog from "posthog-js";
import { TRACKING_EVENTS } from "./posthog";
import { getPageViewEventId, newEventId, sendServerEvent } from "./meta-capi";
// Verrou « hors cible » — cf. ./ad-tracking : rien ne sort vers les régies
// depuis les pages de sortie, ni pour un événement marqué qualified: false.
import { isAdTrackingMuted } from "./ad-tracking";
import { getVisitorId, sendOpenAiServerEvent } from "./openai-capi";
import { getPrefill } from "./lead-prefill";
import { loadLead } from "./funnel-store";

// ============================================================================
// Configuration (depuis les env NEXT_PUBLIC_*, inlinées au build)
// ============================================================================

export const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY ?? "";
export const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "";

export const META_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID ?? "";

// Google Ads : ID de compte "AW-XXXXXXXXX" + labels de conversion "AW-XXX/abc123"
export const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID ?? "";

// OpenAI Ads — pixel de base (PageView à l'init). Vide = désactivé.
export const OPENAI_PIXEL_ID = process.env.NEXT_PUBLIC_OPENAI_PIXEL_ID ?? "";
// Journalisation console du SDK. Utile pour vérifier que le pixel part ;
// laissé à "0" en prod, ça n'a rien à faire dans la console d'un visiteur.
const OPENAI_PIXEL_DEBUG = process.env.NEXT_PUBLIC_OPENAI_PIXEL_DEBUG === "1";
const GADS_LABEL_LEAD = process.env.NEXT_PUBLIC_GOOGLE_ADS_LABEL_LEAD ?? "";
const GADS_LABEL_APPLICATION =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_LABEL_APPLICATION ?? "";

// ============================================================================
// Initialisation des pixels
// ============================================================================

export const initMetaPixel = () => {
  if (!META_PIXEL_ID || typeof window === "undefined") return;
  if (isAdTrackingMuted()) return;
  if ((window as any).fbq) return;

  // @ts-ignore
  !function(f,b,e,v,n,t,s)
  // @ts-ignore
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  // @ts-ignore
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  // @ts-ignore
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  // @ts-ignore
  n.queue=[];t=b.createElement(e);t.async=!0;
  // @ts-ignore
  t.src=v;s=b.getElementsByTagName(e)[0];
  // @ts-ignore
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');

  // @ts-ignore
  window.fbq("init", META_PIXEL_ID);
  // PageView avec event_id partagé → dédupliqué avec l'event serveur (CAPI).
  // @ts-ignore
  window.fbq("track", "PageView", {}, { eventID: getPageViewEventId() });
};

export const initGoogleAds = () => {
  if (!GOOGLE_ADS_ID || typeof window === "undefined") return;
  if (isAdTrackingMuted()) return;
  if ((window as any).__gadsLoaded) return;
  (window as any).__gadsLoaded = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(s);

  (window as any).dataLayer = (window as any).dataLayer || [];
  // gtag DOIT pousser les `arguments` bruts dans dataLayer
  function gtag(...args: any[]) {
    (window as any).dataLayer.push(args);
  }
  (window as any).gtag = gtag;
  gtag("js", new Date());
  gtag("config", GOOGLE_ADS_ID);
};

export const initOpenAiPixel = () => {
  if (!OPENAI_PIXEL_ID || typeof window === "undefined") return;
  if (isAdTrackingMuted()) return;
  if ((window as any).oaiq) return;

  // File d'attente locale : les appels passés avant le chargement du SDK sont
  // empilés puis rejoués. Même principe que fbq.
  const w = window as any;
  const q: any = function (...args: any[]) {
    q.q.push(args);
  };
  q.q = [];
  w.oaiq = q;

  const s = document.createElement("script");
  s.async = true;
  s.src = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
  document.head.appendChild(s);

  // external_id dès le premier événement, même anonyme : le même identifiant
  // part haché par le relais serveur, OpenAI relie les deux flux.
  w.oaiq("init", { pixelId: OPENAI_PIXEL_ID, debug: OPENAI_PIXEL_DEBUG, user: openAiUser({}) });
  measureOpenAiPageView();
};

// ============================================================================
// Identité transmise aux régies (« advanced matching »)
//   Sans elle, Meta et OpenAI ne rattachent un lead à un compte que par leurs
//   cookies — souvent absents (iOS, bloqueurs, navigation privée). Dès qu'on
//   connaît la personne (lead en session, lien pré-rempli, réservation), on la
//   donne aux deux pixels ; ils la hachent eux-mêmes (SHA-256) avant envoi.
//   Jamais pour un profil hors cible (verrou ad-tracking + lead non qualifié).
// ============================================================================

type AdIdentity = { email?: string; phone?: string; firstName?: string; lastName?: string };

// Lien pré-rempli : « 06… » → « +336… » (sans indicatif, le hachage téléphone
// des régies ne correspond à rien). Le formulaire, lui, normalise déjà.
const e164 = (raw?: string) => {
  const d = (raw ?? "").replace(/[\s.\-()]/g, "");
  if (!d) return undefined;
  if (d.startsWith("+")) return d;
  if (d.startsWith("00")) return "+" + d.slice(2);
  if (/^0\d{9}$/.test(d)) return "+33" + d.slice(1);
  return d;
};

function knownIdentity(props?: Record<string, any>): AdIdentity {
  const lead = loadLead();
  if (lead && lead.qualified === false) return {};
  const pre = getPrefill();
  const name = (lead?.nomComplet || pre.nomComplet || "").trim();
  const [firstName, ...rest] = name ? name.split(/\s+/) : [];
  return {
    email: (props?.email as string) || lead?.email || pre.email || undefined,
    phone: lead?.phone || e164(pre.phone) || undefined,
    firstName: firstName || undefined,
    lastName: rest.join(" ") || undefined,
  };
}

// Champs du SDK OpenAI : valeurs brutes acceptées, hachées par le SDK.
function openAiUser(id: AdIdentity): Record<string, string> {
  const u: Record<string, string> = {};
  const vid = getVisitorId();
  if (vid) u.external_id_sha256 = vid;
  if (id.email) u.email_sha256 = id.email.trim().toLowerCase();
  if (id.phone) u.phone_number_sha256 = id.phone;
  if (id.firstName) u.first_name_sha256 = id.firstName;
  if (id.lastName) u.last_name_sha256 = id.lastName;
  return u;
}

let appliedIdentity = "";

/** Donne l'identité connue aux pixels Meta et OpenAI (idempotent). */
export function applyAdIdentity(props?: Record<string, any>) {
  if (typeof window === "undefined" || isAdTrackingMuted()) return;
  const id = knownIdentity(props);
  if (!id.email && !id.phone) return;
  const key = JSON.stringify(id);
  if (key === appliedIdentity) return;
  appliedIdentity = key;
  const w = window as any;

  // Meta : un second `init` du même pixel met à jour l'identité (tant que la
  // première n'en portait pas). Le pixel normalise et hache em/ph/fn/ln.
  if (META_PIXEL_ID && w.fbq) {
    const am: Record<string, string> = {};
    if (id.email) am.em = id.email.trim().toLowerCase();
    if (id.phone) am.ph = id.phone.replace(/\D/g, "");
    if (id.firstName) am.fn = id.firstName.toLowerCase();
    if (id.lastName) am.ln = id.lastName.toLowerCase();
    w.fbq("init", META_PIXEL_ID, am);
  }
  // OpenAI : `init` rappelé = mise à jour de l'identité du pixel.
  if (OPENAI_PIXEL_ID && w.oaiq) {
    w.oaiq("init", { pixelId: OPENAI_PIXEL_ID, debug: OPENAI_PIXEL_DEBUG, user: openAiUser(id) });
  }
}

// Dernier chemin pour lequel un `page_viewed` est parti. Le funnel navigue en
// SPA : sans ce repère, on émettrait un seul page_viewed au chargement initial
// et plus rien ensuite — ou deux sur la première page, selon l'ordre de montage
// des effets React (les effets enfants passent avant ceux de providers.tsx).
let lastOpenAiPath: string | null = null;

/** `page_viewed` au plus une fois par chemin. Le SDK ne l'émet pas tout seul. */
const measureOpenAiPageView = () => {
  const w = window as any;
  if (!w.oaiq) return;
  const path = window.location.pathname;
  if (path === lastOpenAiPath) return;
  lastOpenAiPath = path;

  // Même id des deux côtés => l'action n'est comptée qu'une fois.
  const eventId = newEventId();
  w.oaiq("measure", "page_viewed", { type: "contents" }, { event_id: eventId });
  // Le miroir serveur porte l'external_id haché : c'est lui qui fait remonter
  // la couverture d'identité, y compris sur les visiteurs anonymes.
  // Si le visiteur s'est déjà identifié dans cette session, on joint son
  // identité aux vues suivantes : la couverture email d'OpenAI compte TOUS les
  // événements, or un page_viewed anonyme n'aura jamais d'email.
  const lead = loadLead();
  sendOpenAiServerEvent("page_viewed", "contents", eventId, {
    email: lead?.email,
    phone: lead?.phone,
  });
};

export const initPixels = () => {
  initMetaPixel();
  initGoogleAds();
  initOpenAiPixel();
  // Visiteur déjà connu (lead en session, lien pré-rempli) : identité d'emblée.
  applyAdIdentity();
};

// ============================================================================
// Mapping événement -> conversions Meta / Google Ads
//   PostHog reçoit TOUS les événements. Meta & Google Ads ne reçoivent que les
//   conversions configurées ci-dessous.
// ============================================================================

export type ConversionConfig = {
  metaEvent?: string; // Standard event Meta (Lead, SubmitApplication…)
  gadsLabel?: string; // Conversion label Google Ads (AW-XXX/abc)
  // Événement standard OpenAI Ads. Le SDK impose un `type` de payload par
  // événement (cf. OPENAI_EVENT_TYPES) ; s'en écarter fait rejeter l'envoi.
  openaiEvent?: string;
};

// Événement OpenAI -> forme de payload attendue. Table reprise du SDK.
const OPENAI_EVENT_TYPES: Record<string, string> = {
  page_viewed: "contents",
  lead_created: "customer_action",
  registration_completed: "customer_action",
  appointment_scheduled: "customer_action",
};

export const CONVERSION_MAP: Record<string, ConversionConfig> = {
  // Vue de page : OpenAI seul. Meta et Google ont déjà leur propre PageView.
  [TRACKING_EVENTS.PAGE_VIEW]: {
    openaiEvent: "page_viewed",
  },
  [TRACKING_EVENTS.LEAD_QUALIFIED]: {
    metaEvent: "Lead",
    gadsLabel: GADS_LABEL_LEAD,
    openaiEvent: "lead_created",
  },
  // Inscription à la masterclass : c'est une inscription à un événement, pas
  // un abonnement payant (`subscription_created` attend un plan_enrollment).
  [TRACKING_EVENTS.SEAT_CONFIRMED]: {
    openaiEvent: "registration_completed",
  },
  // Newsletter : une inscription, pas un lead commercial — distincte de
  // `Lead` pour ne pas brouiller l'optimisation des campagnes de RDV.
  [TRACKING_EVENTS.NEWSLETTER_SIGNUP]: {
    metaEvent: "CompleteRegistration",
    openaiEvent: "lead_created",
  },
  // Ressources de la masterclass reçues par email : même famille que la
  // newsletter (inscription), pas un lead commercial.
  [TRACKING_EVENTS.TEMPLATES_REQUESTED]: {
    metaEvent: "CompleteRegistration",
    openaiEvent: "lead_created",
  },
  // Communauté WhatsApp : un clic vers le groupe. Meta `Contact` seul ; aucun
  // événement OpenAI ne décrit ce geste sans le confondre avec un lead.
  [TRACKING_EVENTS.WHATSAPP_JOIN]: {
    metaEvent: "Contact",
  },
  [TRACKING_EVENTS.APPLICATION_SUBMITTED]: {
    metaEvent: "SubmitApplication",
    gadsLabel: GADS_LABEL_APPLICATION,
    openaiEvent: "appointment_scheduled",
  },
};

// ============================================================================
// API publique
// ============================================================================

/** Enregistre des super-properties PostHog (jointes à TOUS les events, persistées). */
export const registerSuperProps = (props: Record<string, any>) => {
  if (posthog && props && Object.keys(props).length) posthog.register(props);
};

/** Identifie l'utilisateur (PostHog) — à appeler dès qu'on a l'email. */
export const identifyLead = (
  email: string,
  properties?: Record<string, any>
) => {
  if (posthog) posthog.identify(email, properties);
};

const PII_KEYS = new Set(["email", "mail", "phone", "num", "tel", "name", "nom", "nomComplet", "prenom"]);
const withoutPii = (props?: Record<string, any>) =>
  props ? Object.fromEntries(Object.entries(props).filter(([k]) => !PII_KEYS.has(k))) : props;

/** Envoie un événement vers PostHog + (si conversion) Meta Pixel + Google Ads. */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, any>
) => {
  // 1) PostHog — tous les événements
  if (posthog) posthog.capture(eventName, properties);

  if (typeof window === "undefined") return;

  // Verrou « hors cible » : jamais d'info sur un profil non qualifié aux régies.
  if (properties?.qualified === false || isAdTrackingMuted()) return;

  const conversion = CONVERSION_MAP[eventName];
  if (!conversion) return;

  // Jamais de donnée personnelle EN CLAIR dans les paramètres des pixels :
  // Meta et Google les traitent comme des paramètres personnalisés (et Meta
  // signale le pixel). L'identité ne part que hachée, par les relais serveur.
  const adProps = withoutPii(properties);

  // Conversion = la personne vient de se faire connaître : identité aux pixels
  // AVANT l'envoi, pour que cet événement-là soit apparié.
  if (conversion.metaEvent || (conversion.openaiEvent && conversion.openaiEvent !== "page_viewed")) {
    applyAdIdentity(properties);
  }

  // 2) Meta Pixel + miroir serveur (CAPI), même event_id → compté une fois.
  //    Le serveur porte l'email et le téléphone hachés : il rattache la
  //    conversion même quand le pixel est bloqué.
  if (conversion.metaEvent) {
    const eventId = newEventId();
    if ((window as any).fbq) {
      (window as any).fbq("track", conversion.metaEvent, adProps, { eventID: eventId });
    }
    const id = knownIdentity(properties);
    sendServerEvent(conversion.metaEvent, {
      eventId,
      userData: { email: id.email, phone: id.phone, external_id: getVisitorId() },
      customData: adProps,
    });
  }

  // 3) Google Ads
  if (conversion.gadsLabel && (window as any).gtag) {
    (window as any).gtag("event", "conversion", {
      send_to: conversion.gadsLabel,
      ...adProps,
    });
  }

  // 4) OpenAI Ads
  if (conversion.openaiEvent) {
    // Les effets enfants se montent avant ceux de providers.tsx : au premier
    // rendu, `page_view` peut partir avant initPixels(). initOpenAiPixel() est
    // idempotent, on s'assure simplement que la file existe.
    initOpenAiPixel();
    const oaiq = (window as any).oaiq;
    if (!oaiq) return;

    if (conversion.openaiEvent === "page_viewed") {
      measureOpenAiPageView();
      return;
    }

    // Sur le funnel masterclass, l'inscription est déjà comptée par
    // `seat_confirmed` -> `registration_completed`. Y ajouter `lead_created`
    // ferait remonter deux conversions pour un seul geste, et fausserait
    // l'optimisation des campagnes.
    const dejaCompte =
      eventName === TRACKING_EVENTS.LEAD_QUALIFIED &&
      properties?.funnel === "masterclass";
    if (dejaCompte) return;

    const dataType = OPENAI_EVENT_TYPES[conversion.openaiEvent] ?? "custom";
    const eventId = newEventId();
    oaiq("measure", conversion.openaiEvent, { type: dataType }, { event_id: eventId });

    // Miroir serveur : il hashe l'identité (email, téléphone, external_id) que
    // le pixel navigateur ne transmet pas. Sur une conversion, le lead est
    // connu — c'est là que la couverture email se joue.
    const lead = loadLead();
    sendOpenAiServerEvent(conversion.openaiEvent, dataType, eventId, {
      email: (properties?.email as string) ?? lead?.email,
      phone: lead?.phone,
    });
  }
};

/** distinct_id PostHog du navigateur (inspecteur /admin/donnees). */
export const getPosthogDistinctId = (): string | undefined => {
  try {
    return posthog?.get_distinct_id?.() || undefined;
  } catch {
    return undefined;
  }
};

/** Oublie l'identité PostHog (tests) : nouveau distinct_id, super-props effacées. */
export const resetPosthog = () => {
  try {
    posthog?.reset?.();
  } catch {
    /* PostHog bloqué : rien à réinitialiser */
  }
};
